import type {
	DocumentStructureSortBy,
	DocumentStructureSortDirection,
} from "@msuite/utils/documents";
import type {
	DocumentStructure,
	DocumentStructureItem,
	Nullable,
	Tables,
} from "@msuite/utils/types";
import { createContext, useContext } from "react";
import type { RefetchFn } from "../../query";
import type { UseRemoteFileUrl } from "../../types";
import type { FileExplorerSelectColumn } from "./shared";

/** Interface */
export interface IFileExplorerContext<T extends { file: Tables<"files"> }> {
	setSearchValue: (value: string) => void;
	focusedItem: DocumentStructureItem<T> | null;
	setFocusedItem: (item: DocumentStructureItem<T> | null) => void;
	useRemoteFileUrl: UseRemoteFileUrl;
	selectedFiles: string[];
	setSelectedFiles: (files: string[]) => void;
	/** The tree as it is rendered — sorted, and narrowed by whatever is in the search box. */
	structure: Nullable<DocumentStructure<T>>;
	/**
	 * Every file the explorer holds, by id, from the tree before the search narrows it.
	 *
	 * A selection outlives the search box: tick a colleague's file, type into the search, and that
	 * file is out of `structure` while still in `selectedFiles`. Anything that decides what a batch
	 * is allowed to do resolves the selection through here, or it decides about the half of the
	 * batch that happens to be on screen.
	 */
	filesById: Map<string, Tables<"files">>;
	refetch: RefetchFn;
	onDeleteFiles: (fileIds: string[]) => void | Promise<void>;
	onDownloadFiles: (fileIds: string[]) => void | Promise<void>;
	handleOnAfterFileAction: () => void | Promise<void>;
	handleOnFolderDrop: (newPath: string, fileIds: string[]) => void | Promise<void>;
	sortBy: DocumentStructureSortBy;
	setSortBy: (sortBy: DocumentStructureSortBy) => void;
	sortByDirection: DocumentStructureSortDirection;
	setSortByDirection: (sortByDirection: DocumentStructureSortDirection) => void;
	/** The editable select columns this explorer renders, in the order they appear. */
	selectColumns?: FileExplorerSelectColumn<T>[];
	canUpload: boolean;
	canUpdateFile: (file: Tables<"files">) => boolean;
	canDeleteFile: (file: Tables<"files">) => boolean;
}

/** Context */
export const FileExplorerContext = createContext<IFileExplorerContext<any>>(
	{} as IFileExplorerContext<any>,
);

/** Hooks */
export function useFileExplorerContext<T extends { file: Tables<"files"> }>() {
	return useContext(FileExplorerContext) as IFileExplorerContext<T>;
}
